import { CURRENCY_CODE, CURRENCY_SYMBOLS } from '../../enum/constants';

export interface PriceFieldValue {
  amount: number | null;
  currency: string;
}

export interface CurrencyOption {
  value: string;
  label: string;
}

const PRICE_AMOUNT_PATTERN = /^(?:\d+(?:[.,]\d{0,2})?|[.,]\d{1,2})$/;
const SUPPORTED_CURRENCIES = new Set(CURRENCY_CODE.map(currency => String(currency.value)));

export function isPriceFieldValue(value: unknown): value is PriceFieldValue {
  return !!value && typeof value === 'object' && !Array.isArray(value) && 'amount' in value && 'currency' in value;
}

export function isSupportedCurrency(value: unknown): value is string {
  return typeof value === 'string' && SUPPORTED_CURRENCIES.has(value);
}

export function getCurrencyOptions(): CurrencyOption[] {
  return CURRENCY_CODE.map(currency => ({
    value: String(currency.value),
    label: `${currency.name} ${CURRENCY_SYMBOLS[String(currency.value)] ?? ''}`,
  }));
}

export function normalizePriceAmount(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;

  const source = typeof value === 'number' ? String(value) : value;
  const normalized = source.trim().replace(/\s+/g, '').replace(',', '.').replace(/^\./, '0.').replace(/\.$/, '');
  if (!PRICE_AMOUNT_PATTERN.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
}

export function formatMoneyAmount(amount: unknown, currency: unknown): string {
  const numericAmount = typeof amount === 'number' ? amount : normalizePriceAmount(amount);
  if (typeof numericAmount !== 'number' || !Number.isFinite(numericAmount)) return '-';
  const [integerPart, fractionPart] = numericAmount.toFixed(2).split('.');
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const code = typeof currency === 'string' ? currency : 'UAH';
  const symbol = CURRENCY_SYMBOLS[code] || code;
  return `${groupedInteger}.${fractionPart} ${symbol}`;
}

export function formatPriceFieldValue(value: unknown): string {
  if (!isPriceFieldValue(value)) return formatScalarValue(value);
  return formatMoneyAmount(value.amount, value.currency);
}

function formatScalarValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') return '-';
  return String(value).trim() || '-';
}
